import React, { useContext, useState, useEffect } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import makeStyles from '@material-ui/core/styles/makeStyles';
import makeSourceCreateBaseUrl from 'pages/Directory/helpers/makeSourceCreateBaseUrl';
import { setSourceSyncEnabled, saveSource, queueSourceSync } from 'store/sources';
import useSource from 'pages/Directory/helpers/useSource';
import SourceContext from 'helpers/SourceContext';
import CardHeader from '@material-ui/core/CardHeader';
import Button from '@material-ui/core/Button';
import Base from 'pages/Directory/Base';
import Switch from '@material-ui/core/Switch';
import { HOME_URL, DIRECTORIES_URL, SOURCE_URL } from 'components/Router/consts';
import {
  UPLOAD_CSV_URL,
  AZURE_SOURCE_TYPE_KEY,
  CSV_SOURCE_TYPE_KEY,
  MAP_ATTRIBUTES_URL,
  GOOGLE_SOURCE_TYPE_KEY,
} from 'pages/Directory/consts';
import {
  REQUIRED_ATTRS_FOR_OTHER_SOURCES,
  REQUIRED_ATTRS_FOR_PRIMARY_SOURCE,
} from 'components/Mappings/_helpers/areMappingsInvalid';

interface Props {
  lastDataCSV?: any;
  mappingAttributes?: any[];
}

function SyncSettings({ lastDataCSV, mappingAttributes = [] }: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const history = useHistory();
  const csvUploaded = !_isEmpty(lastDataCSV);
  const sourceContext = useContext(SourceContext);
  const { sourceInfo } = sourceContext;
  const { directoryId, sourceId, type } = sourceInfo;
  const source = useSource(sourceId);
  const isCsvSource = type === CSV_SOURCE_TYPE_KEY;
  const [submitting, setSubmitting] = useState(false);
  const csvOptOut = _get(source, ['setupProgress', 'csvOptOut'], false);

  const initialSyncState = !(isCsvSource && !csvUploaded);
  const [syncEnabled, setSyncEnabled] = useState(initialSyncState);
  const dispatch = useDispatch();
  const allRequiredMappingsSet = source.isPrimary
    ? _get(mappingAttributes, 'recommendedAttributes', []).filter((attr: any) =>
        REQUIRED_ATTRS_FOR_PRIMARY_SOURCE.includes(_get(attr, ['mapping', 'destination'])),
      ).length === REQUIRED_ATTRS_FOR_PRIMARY_SOURCE.length
    : _get(mappingAttributes, 'recommendedAttributes', []).filter((attr: any) =>
        REQUIRED_ATTRS_FOR_OTHER_SOURCES.includes(_get(attr, ['mapping', 'destination'])),
      ).length === REQUIRED_ATTRS_FOR_OTHER_SOURCES.length;

  const baseUrl = makeSourceCreateBaseUrl(directoryId, sourceId);
  const disableSync = isCsvSource && !csvUploaded;
  const currentStep = type === AZURE_SOURCE_TYPE_KEY ? 4 : 3;

  useEffect(() => {
    if (isCsvSource && !csvUploaded && !csvOptOut) {
      history.push(`${baseUrl}${UPLOAD_CSV_URL}`);
    }

    if (isCsvSource && !csvOptOut && !allRequiredMappingsSet) {
      history.push(`${baseUrl}${MAP_ATTRIBUTES_URL}`);
    }

    if (
      (type === AZURE_SOURCE_TYPE_KEY || type === GOOGLE_SOURCE_TYPE_KEY) &&
      !allRequiredMappingsSet
    ) {
      history.push(`${baseUrl}${MAP_ATTRIBUTES_URL}`);
    }
  });

  const handleBack = () => {
    history.push(`${baseUrl}${MAP_ATTRIBUTES_URL}`);
  };

  const handleChangeSyncSetting = () => {
    setSyncEnabled(!syncEnabled);
  };

  const handleSyncLater = async () => {
    setSubmitting(true);
    handleCompleteWizard();
  };

  const handleQueueSync = async () => {
    if (isCsvSource) {
      await dispatch(queueSourceSync(sourceId, _get(lastDataCSV, 'id')));
    } else {
      await dispatch(queueSourceSync(sourceId));
    }
  };

  const handleNext = async () => {
    setSubmitting(true);
    await dispatch(setSourceSyncEnabled(sourceId, syncEnabled));
    syncEnabled && handleQueueSync();
    handleCompleteWizard();
  };

  const handleCompleteWizard = async () => {
    await dispatch(
      saveSource({
        ...source,
        setupProgress: {
          ..._get(source, 'setupProgress', {}),
          isComplete: true,
        },
      }),
    );

    history.push(`${HOME_URL}${DIRECTORIES_URL}/${directoryId}${SOURCE_URL}/${sourceId}`);

    analytics.track('Wizard - Sync Step Completed', {
      category: 'AD - Directories',
      label: type,
    });
  };

  const description = !isCsvSource ? (
    <>
      Do you want to enable automatic sync? If enabled, Sift will retrieve new data from your data
      source hourly. If you’re not ready to sync now, you can leave this off, and sync later.
    </>
  ) : csvOptOut ? (
    <>
      You’ve successfully set up your data source. When you’re ready to run your first sync, you can
      do so by uploading a CSV or setting up an SFTP integration.
    </>
  ) : (
    <>
      Click the “Initialize Sync” button below to import the data from your CSV file. Your sync
      should be completed in the next few minutes. If you’d like to make further changes or use a
      new file, you can also{' '}
      <Button
        onClick={handleSyncLater}
        className={classNames(classes.syncLater, { [classes.disabled]: submitting })}
        variant="text"
      >
        sync later
      </Button>
      .
    </>
  );

  const renderNextButtonText = () => {
    if (isCsvSource) {
      return csvOptOut ? 'Done' : 'Initialize Sync';
    }

    return syncEnabled ? 'Confirm and Initialize Sync' : 'Confirm and Sync Later';
  };

  return (
    <Base
      onBack={handleBack}
      onNext={handleNext}
      buttonText={renderNextButtonText()}
      currentStep={currentStep}
      disableNextButton={submitting}
    >
      <CardHeader
        title={'Initialize Your Sync'}
        subheader={
          'Run your first data sync to import your people data and see it in Sift within minutes!'
        }
      />
      <div className={classes.midCard}>
        <div className={classes.info}>{description}</div>
        {!isCsvSource && (
          <form className={classes.switchContainer}>
            <label htmlFor="sync-on-off-switch">OFF</label>
            <Switch
              checked={syncEnabled}
              onChange={handleChangeSyncSetting}
              disabled={disableSync}
              value={syncEnabled}
              inputProps={{ 'aria-label': 'sync-on-off-switch' }}
              name="sync-on-off-switch"
            />
            <label htmlFor="sync-on-off-switch">ON</label>
          </form>
        )}
      </div>
    </Base>
  );
}

const useStyles = makeStyles(theme => ({
  info: {
    fontSize: theme.typography.subtitle1.fontSize,
    lineHeight: theme.typography.subtitle1.lineHeight,
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(2.875),
    maxWidth: 903,
    maxHeight: 59,
    letterSpacing: 0.5,
  },
  midCard: {
    display: 'flex',
    marginBottom: theme.spacing(1.875),
  },
  switchContainer: {
    minWidth: 117,
    maxHeight: 48,
    marginLeft: 'auto',
    marginRight: theme.spacing(3.5),
  },
  syncLater: {
    ...theme.typography.body1,
    color: theme.palette.secondary.main,
    cursor: 'pointer',
    textTransform: 'lowercase',
    fontWeight: 'normal',
    padding: 0,
    marginTop: -theme.spacing(0.34),
  },
  disabled: {
    color: theme.palette.action.disabled,
    cursor: 'not-allowed',
  },
}));

export default SyncSettings;
